"use client";

import * as React from "react";
import { Check, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RangeInput } from "@/components/ui/range-input";
import { Textarea } from "@/components/ui/textarea";
const steps = [{
  num: "01",
  label: "PASSENGER DETAILS"
}, {
  num: "02",
  label: "TRIP SPECIFICS"
}, {
  num: "03",
  label: "SERVICE OPTIONS"
}, {
  num: "04",
  label: "CONFIRM"
}];
const locations = ["Los Angeles International (LAX)", "Burbank Airport (BUR)", "Private Aviation Terminal", "Point-to-Point Transfer"];
const genres = ["Airport Transfer", "Corporate Travel", "Hourly Charter", "VIP Event", "Wedding Service", "Executive SUV", "Luxury Sedan", "Group Transport", "Discreet Travel", "City Tour"];
function StepIndicator({
  currentStep
}: {
  currentStep: number;
}) {
  return <div className="w-full mb-16">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 md:gap-y-0">
        {steps.map((step, i) => {
        const isActive = i === currentStep;
        const isComplete = i < currentStep;
        const isFuture = i > currentStep;
        return <div key={step.num} data-index={i} className={`flex flex-col items-start relative ${isFuture ? "opacity-30" : "opacity-100"} transition-opacity duration-500`}>
              <div className="font-sans text-[11px] tracking-[0.2em] mb-3 text-muted-foreground" style={{
            color: isActive ? "var(--color-primary)" : undefined
          }}>
                {step.num} — {step.label}
              </div>
              <div className="flex items-center w-full">
                <div className="relative flex items-center justify-center w-4 h-4 shrink-0">
                  {isComplete ? <div className="w-4 h-4 rounded-lg flex items-center justify-center bg-primary">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div> : isActive ? <div className="w-3 h-3 rounded-lg bg-primary" /> : <div className="w-3 h-3 rounded-lg border border-border" />}
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-px ml-3 hidden md:block bg-border relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 transition-all duration-700 bg-primary" style={{
                width: isComplete ? "100%" : "0%"
              }} />
                  </div>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function UnderlineField({
  label,
  children,
  error
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
}) {
  return <FormItem className="gap-0">
      <FormLabel className="font-sans text-[11px] tracking-[0.2em] text-primary uppercase mb-3">
        {label}
      </FormLabel>
      <FormControl>{children as React.ReactElement}</FormControl>
      {error && <FormMessage className="mt-2 font-sans text-[11px] tracking-wider" />}
    </FormItem>;
}
function InquiryFormContent() {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [direction, setDirection] = React.useState<"forward" | "backward">("forward");
  const [animating, setAnimating] = React.useState(false);
  const [selectedGenres, setSelectedGenres] = React.useState<string[]>([]);
  const [experienceYears, setExperienceYears] = React.useState(5);
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const goToStep = (next: number) => {
    if (animating) return;
    setDirection(next > currentStep ? "forward" : "backward");
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(next);
      setAnimating(false);
    }, 250);
  };
  const toggleGenre = (g: string) => {
    setSelectedGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-white p-10 md:p-16 text-center rounded-lg">
        <div className="font-sans text-[11px] tracking-[0.2em] text-primary mb-6">RESERVATION RECEIVED</div>
        <h3 className="font-display text-3xl md:text-4xl text-foreground mb-6">Thank you.</h3>
        <p className="font-sans text-muted-foreground mb-8 max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-sans text-[11px] tracking-[0.2em] uppercase">
          New Booking
        </Button>
      </div>;
  }
  const slideClass = animating ? direction === "forward" ? "opacity-0 -translate-x-20" : "opacity-0 translate-x-20" : "opacity-100 translate-x-0";
  return <>
      <StepIndicator currentStep={currentStep} />

      <div className="border border-border bg-white relative overflow-hidden rounded-lg shadow-soft">
        <Form onSubmit={handleSubmit} className="p-8 md:p-16 space-y-0">
          <div className={`transition-all duration-300 ease-out ${slideClass}`}>
            {currentStep === 0 && <div className="space-y-10">
                <div className="mb-10">
                  <div className="font-sans text-[11px] tracking-[0.2em] text-primary mb-3">STEP 01</div>
                  <h3 className="font-display text-2xl md:text-3xl text-foreground">Passenger details.</h3>
                </div>

                <FormField control={control} name="name" rules={{
              required: "Name is required",
              minLength: {
                value: 2,
                message: "Min 2 characters"
              }
            }} render={({
              field,
              fieldState
            }) => <UnderlineField label="FULL NAME" error={fieldState.error?.message}>
                      <Input placeholder="John Doe" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 pt-1 text-xl md:text-[22px] font-display text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
                    </UnderlineField>} />

                <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email"
              }
            }} render={({
              field,
              fieldState
            }) => <UnderlineField label="EMAIL ADDRESS" error={fieldState.error?.message}>
                      <Input type="email" placeholder="john@example.com" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 pt-1 text-xl md:text-[22px] font-display text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
                    </UnderlineField>} />

                <FormField control={control} name="phone" render={({
              field,
              fieldState
            }) => <UnderlineField label="PHONE NUMBER" error={fieldState.error?.message}>
                      <Input type="tel" placeholder="323-492-6292" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 pt-1 text-xl md:text-[22px] font-display text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" />
                    </UnderlineField>} />

                <FormField control={control} name="product_interest" rules={{
              required: "Please select a location"
            }} render={({
              field,
              fieldState
            }) => <FormItem className="gap-0">
                      <FormLabel className="font-sans text-[11px] tracking-[0.2em] text-primary uppercase mb-4">
                        PICKUP LOCATION
                      </FormLabel>
                      <FormControl>
                        <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {locations.map((loc, i) => <label key={loc} data-index={i} className="flex items-center gap-3 cursor-pointer group py-2">
                              <RadioGroupItem value={loc} className="border-border" />
                              <span className="font-display text-lg text-foreground group-hover:text-primary transition-colors">
                                {loc}
                              </span>
                            </label>)}
                        </RadioGroup>
                      </FormControl>
                      {fieldState.error && <FormMessage className="mt-2 font-sans text-[11px] tracking-wider" />}
                    </FormItem>} />
              </div>}

            {currentStep === 1 && <div className="space-y-10">
                <div className="mb-10">
                  <div className="font-sans text-[11px] tracking-[0.2em] text-primary mb-3">STEP 02</div>
                  <h3 className="font-display text-2xl md:text-3xl text-foreground">Trip specifics.</h3>
                </div>

                <FormField control={control} name="message" render={({
              field,
              fieldState
            }) => <UnderlineField label="TRIP DETAILS & DESTINATION" error={fieldState.error?.message}>
                      <Textarea rows={4} placeholder="Please provide pickup time, date, and destination address..." {...field} className="border-0 border-b border-border bg-transparent px-0 pb-3 pt-1 text-lg md:text-xl font-display text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors resize-none" />
                    </UnderlineField>} />

                <FormField control={control} name="preferred_contact_method" render={({
              field
            }) => <FormItem className="gap-0">
                      <div className="flex items-center justify-between mb-6">
                        <FormLabel className="font-sans text-[11px] tracking-[0.2em] text-primary uppercase">
                          ESTIMATED PASSENGERS
                        </FormLabel>
                        <span className="font-sans text-sm text-foreground">
                          {experienceYears >= 10 ? "10+" : experienceYears}
                        </span>
                      </div>
                      <FormControl>
                        <RangeInput min={1} max={10} step={1} value={experienceYears} onValueChange={v => {
                  setExperienceYears(v);
                  field.onChange(v);
                }} className="w-full h-px bg-border accent-primary" />
                      </FormControl>
                      <div className="flex justify-between mt-3 font-sans text-[10px] tracking-[0.2em] text-muted-foreground">
                        <span>1</span>
                        <span>5</span>
                        <span>10+</span>
                      </div>
                    </FormItem>} />
              </div>}

            {currentStep === 2 && <div className="space-y-10">
                <div className="mb-10">
                  <div className="font-sans text-[11px] tracking-[0.2em] text-primary mb-3">STEP 03</div>
                  <h3 className="font-display text-2xl md:text-3xl text-foreground">Service options.</h3>
                </div>

                <FormField control={control} name="product_interest" render={({
              field
            }) => <FormItem className="gap-0">
                      <FormLabel className="font-sans text-[11px] tracking-[0.2em] text-primary uppercase mb-5">
                        SERVICE TYPE
                      </FormLabel>
                      <FormControl>
                        <div className="flex flex-wrap gap-2">
                          {genres.map((g, i) => {
                    const selected = selectedGenres.includes(g);
                    return <Button type="button" key={g} data-index={i} variant={selected ? "default" : "outline"} onClick={() => {
                      toggleGenre(g);
                      const newGenres = selected ? selectedGenres.filter(x => x !== g) : [...selectedGenres, g];
                      field.onChange(newGenres.join(", "));
                    }} className="font-sans text-[11px] tracking-[0.2em] uppercase h-auto px-4 py-2 rounded-lg">
                                {g}
                              </Button>;
                  })}
                        </div>
                      </FormControl>
                    </FormItem>} />

                <FormField control={control} name="name" render={({
              field,
              fieldState
            }) => <UnderlineField label="SPECIAL REQUESTS" error={fieldState.error?.message}>
                      <Textarea rows={4} placeholder="Child seats, extra luggage, or specific vehicle requests..." {...field} className="border-0 border-b border-border bg-transparent px-0 pb-3 pt-1 text-lg md:text-xl font-display text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 transition-colors resize-none" />
                    </UnderlineField>} />
              </div>}

            {currentStep === 3 && <div className="space-y-8">
                <div className="mb-10">
                  <div className="font-sans text-[11px] tracking-[0.2em] text-primary mb-3">STEP 04</div>
                  <h3 className="font-display text-2xl md:text-3xl text-foreground">Review your booking.</h3>
                </div>

                <div className="border border-border p-8 md:p-10 bg-accent/40 rounded-lg">
                  <div className="flex items-center justify-between mb-8 pb-6 border-b border-border">
                    <div>
                      <div className="font-sans text-[10px] tracking-[0.3em] text-primary mb-2">SUMMARY</div>
                      <div className="font-sans text-[10px] tracking-[0.2em] text-muted-foreground">
                        PHOENIX ENTERPRISE TCP — RESERVATION
                      </div>
                    </div>
                    <div className="font-sans text-[10px] tracking-[0.2em] text-muted-foreground">
                      {new Date().toLocaleDateString()}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {[{
                  label: "NAME",
                  value: "John Doe"
                }, {
                  label: "EMAIL",
                  value: "john@example.com"
                }, {
                  label: "PHONE",
                  value: "323-492-6292"
                }, {
                  label: "LOCATION",
                  value: "Los Angeles International (LAX)"
                }, {
                  label: "SERVICE",
                  value: "Airport Transfer"
                }, {
                  label: "PASSENGERS",
                  value: `${experienceYears >= 10 ? "10+" : experienceYears}`
                }, {
                  label: "NOTES",
                  value: "None"
                }].map((item, i) => <div key={item.label} data-index={i} className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 md:gap-6 pb-4 border-b border-border/50">
                        <div className="font-sans text-[11px] tracking-[0.2em] text-primary uppercase pt-1">
                          {item.label}
                        </div>
                        <div className="font-display text-lg md:text-xl text-foreground break-words">
                          {item.value}
                        </div>
                      </div>)}
                  </div>
                </div>

                {submitError && <div className="border border-red-500/40 bg-red-500/10 p-4 rounded-lg">
                    <p className="font-sans text-[11px] tracking-wider text-red-600 uppercase">{submitError}</p>
                  </div>}
              </div>}
          </div>

          <div className="flex items-center justify-between pt-12 mt-12 border-t border-border">
            {currentStep > 0 ? <Button type="button" variant="ghost" onClick={() => goToStep(currentStep - 1)} className="font-sans text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground hover:bg-transparent px-0">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button> : <div />}

            {currentStep < steps.length - 1 ? <Button type="button" onClick={() => goToStep(currentStep + 1)} className="btn-primary">
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button> : <Button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? "Sending..." : "Confirm Booking"}
              </Button>}
          </div>
        </Form>
      </div>

      <div className="flex items-center justify-center gap-2 mt-10">
        <Lock className="w-3 h-3 text-muted-foreground" />
        <span className="font-sans text-[10px] tracking-[0.3em] text-muted-foreground uppercase">
          Your privacy is our priority
        </span>
      </div>
    </>;
}
export default function Booking() {
  return;
}